import React, { useState, useEffect } from 'react';
import { Phone, CheckCircle, Shield, Star, TreePine } from 'lucide-react';
import { Header, Footer, useQuoteModal, usePageMeta } from '../components/SharedComponents';
import reviewData from '../reviews.json';
import heroImages, { pickHeroImage } from '../heroImages';
import { PAGE_META } from '../seo';

// The reviews grid further down always shows the first three, so the hero
// pulls from the rest — no repeat on screen. Two more get skipped: very short
// ones ("Very good job.") read as filler next to the headline, and Google
// truncates long reviews mid-word, which looks broken quoted in full here.
const gridReviews = reviewData.reviews.slice(0, 3);
const heroReviewPool = reviewData.reviews.filter(
  review =>
    review.text.length >= 60 &&
    !review.text.trimEnd().endsWith('...') &&
    !gridReviews.includes(review)
);

const CleanTreeService = () => {
  const { QuoteModal, openQuoteModal } = useQuoteModal();

  // The first render must match the prerendered HTML exactly or hydration
  // breaks, so both start on a fixed entry and get randomised in an effect —
  // after mount, where the server never sees it. The page still varies per
  // visit; the crawler and the first paint just get entry zero.
  const [heroImage, setHeroImage] = useState(heroImages[0]);
  const [heroReview, setHeroReview] = useState(
    (heroReviewPool.length ? heroReviewPool : reviewData.reviews)[0]
  );

  useEffect(() => {
    setHeroImage(pickHeroImage());
    const pool = heroReviewPool.length ? heroReviewPool : reviewData.reviews;
    setHeroReview(pool[Math.floor(Math.random() * pool.length)]);
  }, []);

  usePageMeta(PAGE_META['/'].title, PAGE_META['/'].description);

  const handleQuoteClick = () => {
    openQuoteModal();
  };

  const handleReviewClick = () => {
    window.open("https://g.page/r/CVs_S3pReiPrEBM/review", "_blank");
  };


  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Header currentPage="home" />

      {/* Clean Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
            
            {/* Main Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <CheckCircle className="w-4 h-4 mr-2" />
                Licensed & Insured in Maryland
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Your Tree Problem?
                <span className="text-green-600 block">Consider It Solved.</span>
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                When your neighbor recommended us, they knew we'd treat your property like our own. That's how we've built our reputation in Maryland - one satisfied customer at a time.
              </p>

              {/* Simple CTA - CONNECTED TO MODAL */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={handleQuoteClick}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg transition-all duration-200"
                >
                  Get a Free Quote
                </button>
                <a
                  href="tel:+14432080747"
                  className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg flex items-center justify-center space-x-2 transition-colors duration-200"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call (443) 208-0747</span>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center justify-center lg:justify-start space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <span className="font-medium">{reviewData.rating.toFixed(1)}/5 Rating</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>Fully Insured</span>
                </div>
              </div>
            </div>

            {/* Simple Visual */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <div className="w-full h-64 rounded-xl overflow-hidden mb-6">
                  <img
                    src={heroImage.src}
                    alt={heroImage.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Latest Google review */}
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-gray-600 italic mb-2">
                    "{heroReview.text}"
                  </p>
                  <p className="text-sm text-gray-500 font-medium">- {heroReview.name}, Google Review</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Services */}
      <section id="services" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What We Do
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Honest tree work. We'll tell you what needs doing and what doesn't.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Tree Removal",
                description: "When a tree needs to come down, we do it right. Safe, clean, and thorough.",
                buttonText: "Get a Removal Quote",
                image: "https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/2893782948520664233.jpg",
                alt: "Safe removal of a hazardous oak tree"
              },
              {
                title: "Storm Damage",
                description: "Tree down at 2 AM? We answer the phone. Emergency response when you need it most.",
                buttonText: "Get Emergency Help",
                image: "https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/9167796078228255619.jpg",
                alt: "Crew clearing a large downed tree"
              },
              {
                title: "Tree Trimming",
                description: "Keep your trees healthy and your yard looking good. Professional pruning that actually helps.",
                buttonText: "Get a Trimming Quote",
                image: "https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/6794577484929289205.jpg",
                alt: "Crown reduction and shaping on a mature tree"
              }
            ].map((service, index) => (
              <div key={index} className="rounded-xl bg-gray-50 overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col">
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.alt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4 flex-1">{service.description}</p>
                  <button
                    onClick={handleQuoteClick}
                    className="text-green-600 hover:text-green-700 font-medium text-sm border-b border-green-600 hover:border-green-700 transition-colors self-center"
                  >
                    {service.buttonText} →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Real People, Real Stories
            </h2>
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-current" />
                ))}
              </div>
              <span className="text-lg font-semibold text-gray-700">{reviewData.rating.toFixed(1)}/5 from {reviewData.count} Google reviews</span>
            </div>
          </div>

          {/* Reviews Grid — real Google reviews from src/reviews.json */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {reviewData.reviews.slice(0, 3).map((review, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <p className="text-sm text-gray-500">Google Review · {review.timeAgo}</p>
                  </div>
                  <div className="flex text-yellow-400">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 italic">"{review.text}"</p>
              </div>
            ))}
          </div>

          {/* See More Reviews Button - CONNECTED TO MODAL */}
          <div className="text-center mb-12">
            <button
              onClick={handleReviewClick}
              className="bg-white border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300"
            >
              Leave A Review
            </button>
          </div>

          {/* Why Choose Us - Simplified */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Licensed & Insured", desc: "Full coverage for your protection" },
              { icon: CheckCircle, title: "Professional Quality", desc: "Experienced, uniformed crew" },
              { icon: Phone, title: "Fast Response", desc: "Same-day service available" },
              { icon: TreePine, title: "Complete Cleanup", desc: "Leave property spotless" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-green-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Tree Emergency? We're Already on Our Way.
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Don't spend another night worrying about that tree. One call gets you peace of mind.
          </p>
          
          {/* CTA Buttons - CONNECTED TO MODAL */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleQuoteClick}
              className="bg-gradient-to-r from-white to-emerald-50 hover:from-emerald-50 hover:to-emerald-100 text-green-700 px-8 py-4 rounded-lg font-semibold text-lg shadow-lg transition-all duration-200"
            >
              Get a Free Quote
            </button>
            <a
              href="tel:+14432080747"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200"
            >
              Call (443) 208-0747
            </a>
          </div>

          <div className="text-green-100 text-sm">
            Serving Anne Arundel, Howard, Baltimore & Carroll counties
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
       {/* Add the QuoteModal component */}
      <QuoteModal />
    </div>
  );
};

export default CleanTreeService;